import React, { useCallback, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { type Sandbox } from "../consts";
import { RemocalStatusIcon } from "../RemocalStatusIcon";
import * as widgetStyles from "../styles_vanilla.css";
import * as styles from "./styles_vanilla.css";

const TYPEAHEAD_RESET_MS = 500;

export type SandboxListboxGroup = {
  /** Group heading; omit for a flat list (e.g. recent sandboxes). */
  labelId?: string;
  label?: string;
  sandboxes: ReadonlyArray<Sandbox>;
  /** When true, every option in the group is disabled (e.g. Inactive in the picker). */
  disabled?: boolean;
};

export type SandboxListboxProps = {
  groups: ReadonlyArray<SandboxListboxGroup>;
  ariaLabel: string;
  /** Prefaces each row with a status icon (recent sandboxes). */
  showStatusIcon?: boolean;
  /** Draws a border around the list (sandbox picker). */
  bordered?: boolean;
  onSwitch: (routingKey: string) => void;
};

const toOptionId = (routingKey: string) =>
  `remocal-sandbox-option-${routingKey.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

type OptionProps = {
  sandbox: Sandbox;
  disabled: boolean;
  isSelected: boolean;
  isActive: boolean;
  showStatusIcon: boolean;
  optionRef: (el: HTMLLIElement | null) => void;
  onActivate: () => void;
};

const SandboxOption: React.FC<OptionProps> = ({
  sandbox,
  disabled,
  isSelected,
  isActive,
  showStatusIcon,
  optionRef,
  onActivate,
}) => {
  const variant = disabled ? "disabled" : isActive ? "active" : "default";
  const mutedTextClass = disabled ? styles.inactiveRowTextStyle : undefined;
  return (
    <li
      ref={optionRef}
      id={toOptionId(sandbox.routingKey)}
      role="option"
      aria-selected={isSelected}
      aria-disabled={disabled || undefined}
      title={disabled ? "This sandbox is inactive" : undefined}
      className={styles.optionVariants[variant]}
      onClick={() => {
        if (!disabled) onActivate();
      }}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onActivate();
        }
      }}
    >
      {showStatusIcon && (
        <RemocalStatusIcon isCurrentSandboxReady={sandbox.ready ?? true} isBaseline={false} />
      )}
      <Text
        asSpan
        variant="strong-md"
        lines={1}
        className={classNames(styles.optionNameStyle, mutedTextClass)}
      >
        {sandbox.name}
      </Text>
      <Flex alignItems="center" gap="10px" flexShrink={0}>
        <Text variant="secondary-md" asSpan lines={1} className={mutedTextClass}>
          {sandbox.routingKey}
        </Text>
        <span
          className={styles.optionArrowVariants[disabled ? "hidden" : "visible"]}
          aria-hidden="true"
        >
          <Icon type="arrow-right" color={Colors.Neutral600} />
        </span>
      </Flex>
    </li>
  );
};

export const SandboxListbox: React.FC<SandboxListboxProps> = ({
  groups,
  ariaLabel,
  showStatusIcon = false,
  bordered = false,
  onSwitch,
}) => {
  const listboxRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const typeaheadRef = useRef({
    query: "",
    timeoutId: undefined as ReturnType<typeof setTimeout> | undefined,
  });
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined);
  const [hasFocus, setHasFocus] = useState(false);

  const enabledKeys = useMemo(
    () =>
      groups.flatMap((group) =>
        group.disabled
          ? []
          : group.sandboxes.filter((sb) => sb.ready ?? true).map((sb) => sb.routingKey),
      ),
    [groups],
  );

  const nameByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of groups) {
      for (const sb of group.sandboxes) {
        map.set(sb.routingKey, sb.name);
      }
    }
    return map;
  }, [groups]);

  // Selection follows focus among enabled options; fall back when the active
  // key is filtered out so aria-activedescendant always points at a valid option.
  const resolvedActiveKey =
    activeKey && enabledKeys.includes(activeKey) ? activeKey : enabledKeys[0];

  const setActiveAndScroll = useCallback((routingKey: string | undefined) => {
    setActiveKey(routingKey);
    if (!routingKey) return;
    requestAnimationFrame(() => {
      optionRefs.current[routingKey]?.scrollIntoView({ block: "nearest" });
    });
  }, []);

  const moveActive = useCallback(
    (direction: 1 | -1) => {
      if (enabledKeys.length === 0) return;
      const currentIndex = resolvedActiveKey ? enabledKeys.indexOf(resolvedActiveKey) : -1;
      const nextIndex =
        currentIndex === -1
          ? 0
          : (currentIndex + direction + enabledKeys.length) % enabledKeys.length;
      setActiveAndScroll(enabledKeys[nextIndex]);
    },
    [enabledKeys, resolvedActiveKey, setActiveAndScroll],
  );

  const moveToEdge = useCallback(
    (edge: "start" | "end") => {
      if (enabledKeys.length === 0) return;
      setActiveAndScroll(
        edge === "start" ? enabledKeys[0] : enabledKeys[enabledKeys.length - 1],
      );
    },
    [enabledKeys, setActiveAndScroll],
  );

  const handleTypeahead = useCallback(
    (character: string) => {
      if (enabledKeys.length === 0) return;
      const { timeoutId } = typeaheadRef.current;
      if (timeoutId !== undefined) clearTimeout(timeoutId);

      const nextQuery = `${typeaheadRef.current.query}${character.toLowerCase()}`;
      typeaheadRef.current.query = nextQuery;
      typeaheadRef.current.timeoutId = setTimeout(() => {
        typeaheadRef.current.query = "";
        typeaheadRef.current.timeoutId = undefined;
      }, TYPEAHEAD_RESET_MS);

      const currentIndex = resolvedActiveKey ? enabledKeys.indexOf(resolvedActiveKey) : -1;
      const searchOrder = [
        ...enabledKeys.slice(currentIndex + 1),
        ...enabledKeys.slice(0, currentIndex + 1),
      ];
      const match = searchOrder.find((key) =>
        (nameByKey.get(key) ?? "").toLowerCase().startsWith(nextQuery),
      );
      if (match) setActiveAndScroll(match);
    },
    [enabledKeys, nameByKey, resolvedActiveKey, setActiveAndScroll],
  );

  const activateActive = useCallback(() => {
    if (resolvedActiveKey) onSwitch(resolvedActiveKey);
  }, [onSwitch, resolvedActiveKey]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          moveActive(1);
          break;
        case "ArrowUp":
          e.preventDefault();
          moveActive(-1);
          break;
        case "Home":
          e.preventDefault();
          moveToEdge("start");
          break;
        case "End":
          e.preventDefault();
          moveToEdge("end");
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          activateActive();
          break;
        default:
          if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            handleTypeahead(e.key);
          }
          break;
      }
    },
    [activateActive, handleTypeahead, moveActive, moveToEdge],
  );

  const activeDescendantId =
    hasFocus && resolvedActiveKey ? toOptionId(resolvedActiveKey) : undefined;

  const visibleGroups = groups.filter((group) => group.sandboxes.length > 0);

  return (
    <div
      ref={listboxRef}
      role="listbox"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-activedescendant={activeDescendantId}
      className={styles.listVariants[bordered ? "bordered" : "plain"]}
      onFocus={() => setHasFocus(true)}
      onBlur={() => setHasFocus(false)}
      onKeyDown={handleKeyDown}
    >
      {visibleGroups.map((group, index) => {
        const groupDisabled = group.disabled ?? false;
        const groupKey = group.labelId ?? `group-${index}`;
        return (
          <React.Fragment key={groupKey}>
            {index > 0 && <div className={styles.groupDividerStyle} role="presentation" />}
            <div
              role={group.labelId ? "group" : undefined}
              aria-labelledby={group.labelId}
              className={styles.groupStyle}
            >
              {group.label && group.labelId && (
                <Text
                  id={group.labelId}
                  variant="upper-sm"
                  className={widgetStyles.sectionLabelStyle}
                >
                  {group.label}
                </Text>
              )}
              <ul className={styles.optionsStyle}>
                {group.sandboxes.map((sb) => {
                  const disabled = groupDisabled || !(sb.ready ?? true);
                  const isSelected = !disabled && sb.routingKey === resolvedActiveKey;
                  return (
                    <SandboxOption
                      key={sb.routingKey}
                      sandbox={sb}
                      disabled={disabled}
                      isSelected={isSelected}
                      isActive={hasFocus && isSelected}
                      showStatusIcon={showStatusIcon}
                      onActivate={() => {
                        setActiveAndScroll(sb.routingKey);
                        listboxRef.current?.focus({ preventScroll: true });
                        onSwitch(sb.routingKey);
                      }}
                      optionRef={(el) => {
                        optionRefs.current[sb.routingKey] = el;
                      }}
                    />
                  );
                })}
              </ul>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};
